import {
  activeBadgeStyle,
  detailCardSx,
  detailInfoCardSx,
  detailPillSx,
  borderRadiusBoxes,
  filterButtonSx,
  filterSectionTitleSx,
  iconAccentButtonSx,
  modelBadgeSx,
  rightPanelTitleColor,
  searchInputSx,
  searchInputWrapperSx,
  searchToolbarStickySx,
  themePalette,
  webkitScrollbar,
  webkitscrollbarTrack,
} from "../../DevicesSettings/styles"

export { borderRadiusBoxes, themePalette }
export const stateTreeGuideColor = "rgba(148, 163, 184, 0.35)"
const stateViewportPanelHeight = "calc(100vh - 7rem)"

export const stateTreeScrollAreaSx = {
  height: stateViewportPanelHeight,
  maxHeight: stateViewportPanelHeight,
  backgroundColor: themePalette.surface.panel,
}

export const stateTreeListStyle = {
  padding: "0.75rem 0 0.75rem 0.75rem",
  margin: 0,
}

export const stateSearchToolbarSx = {
  ...searchToolbarStickySx,
}

export const stateSearchInputWrapperSx = {
  ...searchInputWrapperSx,
}

export const stateSearchInputSx = {
  ...searchInputSx,
}

export const stateFilterButtonSx = {
  ...filterButtonSx,
}

export const stateFilterSectionTitleSx = {
  ...filterSectionTitleSx,
}

export const stateTreeCardBaseStyle = {
  border: `1px solid ${themePalette.border.subtle}`,
  borderRadius: borderRadiusBoxes,
  backgroundColor: themePalette.common.white,
  overflow: "hidden",
}

export const stateTreeBadgeStyle = {
  ...modelBadgeSx,
  flexShrink: 0,
}

export const stateTreeActionButtonStyle = {
  borderRadius: iconAccentButtonSx.borderRadius,
  border: iconAccentButtonSx.border,
  backgroundColor: iconAccentButtonSx.backgroundColor,
  color: iconAccentButtonSx.color,
  boxShadow: iconAccentButtonSx.boxShadow,
  width: "1.95rem",
  height: "1.95rem",
  minWidth: "1.95rem",
}

const stateIntegrityToneStyles = {
  success: {
    backgroundColor: themePalette.status.successSoft,
    color: themePalette.status.success,
    borderColor: "rgba(4, 120, 87, 0.16)",
    boxShadow: "0 4px 10px rgba(4, 120, 87, 0.12)",
  },
  warning: {
    backgroundColor: themePalette.warning.light,
    color: themePalette.warning.dark,
    borderColor: "rgba(180, 83, 9, 0.18)",
    boxShadow: "0 4px 10px rgba(180, 83, 9, 0.10)",
  },
  danger: {
    backgroundColor: themePalette.error.light,
    color: themePalette.error.main,
    borderColor: "rgba(185, 28, 28, 0.16)",
    boxShadow: "0 4px 10px rgba(185, 28, 28, 0.10)",
  },
  neutral: {
    backgroundColor: themePalette.surface.alt,
    color: themePalette.text.disabled,
    borderColor: themePalette.border.subtle,
    boxShadow: "none",
  },
}

export const buildStateIntegrityBadgeStyle = ({ tone = "neutral", isCompact = false } = {}) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: isCompact ? "3.1rem" : "3.35rem",
  height: isCompact ? "1.35rem" : "1.45rem",
  padding: 0,
  borderRadius: borderRadiusBoxes,
  border: `1px solid ${stateIntegrityToneStyles[tone]?.borderColor || stateIntegrityToneStyles.neutral.borderColor}`,
  backgroundColor: stateIntegrityToneStyles[tone]?.backgroundColor || stateIntegrityToneStyles.neutral.backgroundColor,
  color: stateIntegrityToneStyles[tone]?.color || stateIntegrityToneStyles.neutral.color,
  boxShadow: stateIntegrityToneStyles[tone]?.boxShadow || stateIntegrityToneStyles.neutral.boxShadow,
  fontSize: isCompact ? "0.7rem" : "0.76rem",
  fontWeight: 700,
  lineHeight: 1,
  letterSpacing: "0.01em",
  fontVariantNumeric: "tabular-nums",
  textAlign: "center",
  whiteSpace: "nowrap",
  flexShrink: 0,
  boxSizing: "border-box",
  transform: "translateY(-0.5px)",
})

export const stateTreeNestedListStyle = {
  position: "relative",
  padding: "0.45rem 0 0.45rem 1.35rem",
  backgroundColor: themePalette.surface.alt,
  borderTopRightRadius: 0,
  borderBottomRightRadius: 0,
  overflow: "visible",
}

export const stateDetailPanelStyle = {
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  height: stateViewportPanelHeight,
  maxHeight: stateViewportPanelHeight,
  backgroundColor: themePalette.surface.panel,
  boxSizing: "border-box",
  "&::-webkit-scrollbar": {
    ...webkitScrollbar,
  },
  "&::-webkit-scrollbar-track": {
    ...webkitscrollbarTrack,
    background: themePalette.status.scrollbarTrackLight,
  },
  "&::-webkit-scrollbar-thumb": {
    background: themePalette.primary.main,
  },
}

export const stateDetailTitleStyle = {
  display: "flex",
  alignItems: "center",
  gap: "0.75rem",
  padding: "0.75rem 1rem",
  borderBottom: `1px solid ${themePalette.border.main}`,
  background: `linear-gradient(180deg, ${themePalette.common.white} 0%, ${themePalette.surface.soft} 100%)`,
}

export const stateDetailBodyStyle = {
  flex: 1,
  overflowY: "scroll",
  padding: "0.75rem 0.9rem",
  display: "flex",
  flexDirection: "column",
  rowGap: "0.6rem",
  "&::-webkit-scrollbar": {
    ...webkitScrollbar,
  },
  "&::-webkit-scrollbar-track": {
    ...webkitscrollbarTrack,
    background: themePalette.status.scrollbarTrackLight,
  },
  "&::-webkit-scrollbar-thumb": {
    background: themePalette.primary.main,
  },
}

export const stateAlertBadgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  backgroundColor: themePalette.error.main,
  color: themePalette.common.white,
  borderRadius: 20,
  padding: "6px 8px",
  fontWeight: 700,
  cursor: "pointer",
  fontSize: "0.85rem",
  userSelect: "none",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
}

export const buildStateNodeStatusBadgeStyle = (active) => ({
  ...activeBadgeStyle(active),
  flexShrink: 0,
})

export const stateDetailCardSx = {
  ...detailCardSx,
}

export const stateDetailInfoCardSx = {
  ...detailInfoCardSx,
}

export const stateDetailPillSx = {
  ...detailPillSx,
}

export const stateSummaryBadgeSx = {
  ...modelBadgeSx,
}

export const stateSectionTitleSx = {
  fontSize: "0.78rem",
  fontWeight: 700,
  color: rightPanelTitleColor,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  margin: 0,
}

export const stateDetailRowsSx = {
  display: "grid",
  gap: "0.4rem",
}

export const stateDetailRowSx = {
  display: "grid",
  gridTemplateColumns: "minmax(160px, 220px) minmax(0, 1fr)",
  columnGap: "1rem",
  rowGap: "0.05rem",
  alignItems: "start",
}

export const stateDetailLabelSx = {
  fontSize: "0.72rem",
  fontWeight: 700,
  color: themePalette.primary.main,
  textTransform: "uppercase",
  letterSpacing: "0.03em",
  lineHeight: 1.25,
}

export const stateDetailValueSx = {
  fontSize: "0.92rem",
  color: themePalette.text.secondary,
  lineHeight: 1.25,
  wordBreak: "break-word",
}

export const stateSectionHeaderSx = {
  display: "flex",
  alignItems: "center",
  gap: "0.55rem",
}

export const stateSectionIconSx = {
  color: themePalette.primary.main,
  fontSize: "1.05rem",
}

export const stateTabsSx = {
  minHeight: "2.45rem",
  borderBottom: `1px solid ${themePalette.border.main}`,
  marginBottom: "0.1rem",
  "& .MuiTabs-indicator": {
    backgroundColor: themePalette.primary.main,
    height: "3px",
    borderRadius: "999px",
  },
}

export const stateTabSx = {
  textTransform: "none",
  minHeight: "2.45rem",
  minWidth: 0,
  fontSize: "0.84rem",
  fontWeight: 600,
}

export const stateWarningCardSx = {
  ...detailInfoCardSx,
  alignItems: "flex-start",
  backgroundColor: themePalette.warning.light,
  borderColor: themePalette.warning.main,
}
